<template>
    <div
        v-if="isVisible"
        class="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-4"
    >
        <Panel size="full" class="w-full max-w-md">
            <form @submit.prevent="handleSubmit" class="space-y-6">
                <div class="text-center">
                    <h2 class="panel-title mb-2 text-white">{{ $t('onboarding.title') }}</h2>
                    <p class="text-sm text-gray-300">{{ $t('onboarding.subtitle') }}</p>
                </div>

                <div class="text-center">
                    <div class="mb-4">
                        <div
                            class="border-stroke-1 bg-card mx-auto h-24 w-24 overflow-hidden border-2"
                        >
                            <img
                                v-if="avatarPreview"
                                :src="avatarPreview"
                                :alt="$t('onboarding.avatar_preview_alt')"
                                class="h-full w-full object-cover"
                            />
                            <div v-else class="text-titles flex h-full items-center justify-center">
                                <svg class="h-8 w-8" fill="currentColor" viewBox="0 0 24 24">
                                    <path
                                        d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"
                                    />
                                </svg>
                            </div>
                        </div>
                    </div>

                    <input
                        ref="fileInput"
                        type="file"
                        accept="image/*"
                        @change="handleAvatarChange"
                        class="sr-only"
                    />

                    <DiagonalCutButton
                        type="button"
                        variant="secondary"
                        cut-position="left-top"
                        @click="openFilePicker"
                    >
                        {{ $t('onboarding.upload_avatar') }}
                    </DiagonalCutButton>
                </div>

                <TagNameInput
                    v-model="tagName"
                    :label="$t('onboarding.profile_name_label')"
                    :placeholder="$t('onboarding.profile_name_placeholder')"
                    @update:status="tagNameStatus = $event"
                />

                <div>
                    <label for="nationality" class="mb-2 block text-sm font-medium text-white">
                        {{ $t('onboarding.nationality_label') }}
                    </label>
                    <div class="relative">
                        <select
                            id="nationality"
                            v-model="nationality"
                            name="nationality"
                            required
                            class="bg-card w-full appearance-none border border-zinc-700 px-4 py-3 pr-10 text-white transition-colors focus:border-red-500 focus:outline-none"
                        >
                            <option
                                v-for="country in countries"
                                :key="country.code"
                                :value="country.code"
                            >
                                {{ country.name }}
                            </option>
                        </select>
                        <div
                            class="pointer-events-none absolute top-1/2 right-4 -translate-y-1/2 text-gray-400"
                        >
                            <svg class="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                                <path
                                    fill-rule="evenodd"
                                    d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                                    clip-rule="evenodd"
                                />
                            </svg>
                        </div>
                    </div>
                </div>

                <div
                    v-if="errorMessage"
                    class="border border-red-600 bg-red-900/50 p-4"
                    :style="{ clipPath: 'polygon(8px 0, 100% 0, 100% 100%, 0 100%, 0 8px)' }"
                >
                    <div class="text-sm text-red-300">
                        {{ errorMessage }}
                    </div>
                </div>

                <div class="pt-2">
                    <DiagonalCutButton
                        type="submit"
                        :disabled="isLoading || !tagName.trim() || tagNameStatus === 'taken' || tagNameStatus === 'checking'"
                        :loading="isLoading"
                        variant="primary"
                        cut-position="right-top"
                        class="w-full"
                    >
                        {{ isLoading ? $t('onboarding.submitting') : $t('onboarding.submit') }}
                    </DiagonalCutButton>
                </div>
            </form>
        </Panel>
    </div>
</template>

<script>
import { useAuthStore } from '@/stores/auth.js'
import Panel from '@/components/layout/Panel.vue'
import DiagonalCutButton from '@/components/ui/DiagonalCutButton.vue'
import TagNameInput from '@/components/ui/TagNameInput.vue'
import { countries } from '@/constants/countries.js'
import { useImageCompression } from '@/composables/useImageCompression.js'

export default {
    name: 'OnboardingModal',
    components: {
        Panel,
        DiagonalCutButton,
        TagNameInput,
    },
    props: {
        isVisible: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['completed'],
    data() {
        return {
            tagName: '',
            tagNameStatus: 'idle',
            nationality: 'nl',
            avatarFile: null,
            avatarPreview: null,
            isLoading: false,
            errorMessage: '',
            countries,
        }
    },
    methods: {
        openFilePicker() {
            const input = this.$refs.fileInput

            if (!input) {
                console.error('File input not found')
                return
            }

            // Method 1: Try direct click (works in most browsers and iOS WebView)
            try {
                input.click()
            } catch (error) {
                console.warn('Direct click failed, trying alternative method:', error)

                // Method 2: Dispatch click event (fallback for Android WebView)
                try {
                    const clickEvent = new MouseEvent('click', {
                        view: window,
                        bubbles: true,
                        cancelable: true,
                    })
                    input.dispatchEvent(clickEvent)
                } catch (eventError) {
                    console.error('Failed to open file picker:', eventError)
                    this.errorMessage = this.$t('onboarding.error_file_picker')
                }
            }
        },

        async handleAvatarChange(event) {
            const file = event.target.files[0]
            if (!file) return

            const { processImage, createPreview } = useImageCompression()

            const result = await processImage(file, {
                maxSizeMB: 5,
                targetSizeMB: 4.5,
                maxWidthOrHeight: 1920,
            })

            if (!result.success) {
                this.errorMessage = result.error
                event.target.value = ''
                return
            }

            this.avatarFile = result.file
            this.errorMessage = ''

            try {
                this.avatarPreview = await createPreview(result.file)
            } catch (error) {
                console.error('Failed to create preview:', error)
                this.errorMessage = this.$t('onboarding.error_preview_image')
                event.target.value = ''
            }
        },

        async handleSubmit() {
            if (this.isLoading) {
                return
            }

            if (!this.tagName.trim()) {
                this.errorMessage = this.$t('onboarding.error_missing_name')
                return
            }

            if (this.tagNameStatus === 'taken') {
                this.errorMessage = this.$t('onboarding.error_name_taken')
                return
            }

            this.isLoading = true
            this.errorMessage = ''

            try {
                const authStore = useAuthStore()

                const userData = {
                    tag_name: this.tagName.trim(),
                    nationality: this.nationality,
                }

                if (this.avatarFile) {
                    userData.avatar = this.avatarFile
                }

                await authStore.updateUser(userData)
                this.$emit('completed')
            } catch (error) {
                console.error('Onboarding error:', error)

                if (error.response?.data?.errors) {
                    const errors = error.response.data.errors
                    const firstError = Object.values(errors)[0]
                    this.errorMessage = Array.isArray(firstError) ? firstError[0] : firstError
                } else if (error.response?.data?.message) {
                    this.errorMessage = error.response.data.message
                } else {
                    this.errorMessage = this.$t('onboarding.error_generic')
                }
            } finally {
                this.isLoading = false
            }
        },
    },
}
</script>
